import {
  Button,
  Input,
  InputNumber,
  Modal,
  Select,
  Table,
  Tag,
  message,
} from "antd";
import axios from "axios";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../../../../../nestserver";

const authHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const STATUS_TAGS = {
  pending: { color: "gold", label: "Pending" },
  approved: { color: "green", label: "Approved" },
  partially_approved: { color: "blue", label: "Partly approved" },
  rejected: { color: "red", label: "Rejected" },
};

const requesterName = (user) => {
  const profile = user?.teacher || user?.admin;
  const name = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
    : "";
  return name || user?.username || "—";
};

const errorText = (err) =>
  err?.response?.data?.error?.errors?.[0] || "Something went wrong";

/** Admin's queue of instructor stock requests: approve (possibly reduced) or reject. */
const StockRequests = () => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState();
  const [approving, setApproving] = useState(null);
  const [approvedQty, setApprovedQty] = useState({});
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [saving, setSaving] = useState(false);

  const requests = useQuery(["/school-stock/requests", status], async () => {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    const res = await axios.get(
      `${serverUrl}/school-stock/requests?${params.toString()}`,
      { headers: authHeader() },
    );
    return res.data;
  });

  // On-hand quantities, so the approve modal can cap each line at what the school holds.
  const stock = useQuery("/school-stock/items/for-requests", async () => {
    const res = await axios.get(`${serverUrl}/school-stock/items?is_active=all`, {
      headers: authHeader(),
    });
    return res.data;
  });
  const onHand = Object.fromEntries(
    (stock?.data?.result || []).map((item) => [
      String(item._id),
      item.quantity_in_hand,
    ]),
  );

  const refresh = () => {
    queryClient.invalidateQueries("/school-stock/requests");
    queryClient.invalidateQueries("/school-stock/items");
    queryClient.invalidateQueries("/school-stock/items/summary");
    queryClient.invalidateQueries("/school-stock/items/for-requests");
  };

  const openApprove = (record) => {
    const initial = {};
    record.items.forEach((line) => {
      const cap = Math.min(
        line.quantity_requested,
        onHand[String(line.stock_item)] ?? 0,
      );
      initial[line._id] = cap;
    });
    setApprovedQty(initial);
    setApproving(record);
  };

  const handleApprove = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${serverUrl}/school-stock/requests/${approving._id}/approve`,
        {
          items: approving.items.map((line) => ({
            line_id: line._id,
            quantity_approved: approvedQty[line._id] ?? 0,
          })),
        },
        { headers: authHeader() },
      );
      message.success("Request processed and stock updated");
      setApproving(null);
      refresh();
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${serverUrl}/school-stock/requests/${rejecting._id}/reject`,
        { rejection_reason: rejectReason || undefined },
        { headers: authHeader() },
      );
      message.success("Request rejected");
      setRejecting(null);
      setRejectReason("");
      refresh();
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Request",
      width: 130,
      render: (r) => <span className="font-semibold">{r.request_id}</span>,
    },
    {
      title: "Requested by",
      width: 180,
      render: (r) => requesterName(r.requested_by),
    },
    {
      title: "Items",
      render: (r) => (
        <div className="space-y-1">
          {r.items.map((line) => (
            <div key={line._id} className="text-sm">
              {line.item_name} — requested {line.quantity_requested} {line.unit}
              {r.status !== "pending" && (
                <span className="text-gray-500">
                  {" "}
                  (approved {line.quantity_approved})
                </span>
              )}
            </div>
          ))}
          {r.reason && (
            <div className="text-xs text-gray-500">Reason: {r.reason}</div>
          )}
          {r.rejection_reason && (
            <div className="text-xs text-red-600">
              Rejected: {r.rejection_reason}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      width: 140,
      align: "center",
      render: (r) => {
        const tag = STATUS_TAGS[r.status] || { color: "default", label: r.status };
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    {
      title: "Action",
      width: 190,
      align: "center",
      render: (r) =>
        r.status === "pending" ? (
          <div className="flex items-center justify-center gap-2">
            <Button size="small" type="primary" onClick={() => openApprove(r)}>
              Review
            </Button>
            <Button size="small" danger onClick={() => setRejecting(r)}>
              Reject
            </Button>
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
  ];

  return (
    <div className="pt-6">
      <div className="bg-white p-10 rounded-2xl">
      <div className="flex gap-4 pb-4">
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 200 }}
          value={status}
          onChange={setStatus}
          options={Object.entries(STATUS_TAGS).map(([value, t]) => ({
            value,
            label: t.label,
          }))}
        />
      </div>

      <Table
        rowKey="_id"
        className="rounded-2xl"
        loading={requests.isLoading}
        columns={columns}
        dataSource={requests?.data?.result || []}
        scroll={{ x: 900 }}
      />

      <Modal
        title={`Review ${approving?.request_id || ""}`}
        open={!!approving}
        onCancel={() => setApproving(null)}
        onOk={handleApprove}
        okText="Approve"
        confirmLoading={saving}
        destroyOnClose
        width={620}
      >
        <p className="pb-3 text-gray-600">
          Approve each line as requested, or lower it. Lines are capped at what
          is in hand, and approving deducts the approved amount from stock. To
          decline everything, use Reject instead.
        </p>
        {approving?.items.map((line) => {
          const available = onHand[String(line.stock_item)] ?? 0;
          const cap = Math.min(line.quantity_requested, available);
          return (
            <div
              key={line._id}
              className="flex items-center justify-between gap-4 py-2 border-b"
            >
              <div>
                <div className="font-semibold">{line.item_name}</div>
                <div className="text-xs text-gray-500">
                  Requested {line.quantity_requested} {line.unit} · {available}{" "}
                  {line.unit} in hand
                </div>
              </div>
              <InputNumber
                min={0}
                max={cap}
                precision={0}
                value={approvedQty[line._id]}
                onChange={(value) =>
                  setApprovedQty((prev) => ({
                    ...prev,
                    [line._id]: value ?? 0,
                  }))
                }
                disabled={cap === 0}
              />
            </div>
          );
        })}
      </Modal>

      <Modal
        title={`Reject ${rejecting?.request_id || ""}`}
        open={!!rejecting}
        onCancel={() => {
          setRejecting(null);
          setRejectReason("");
        }}
        onOk={handleReject}
        okText="Reject"
        okButtonProps={{ danger: true }}
        confirmLoading={saving}
        destroyOnClose
      >
        <Input.TextArea
          rows={3}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Reason (optional) — shown to the instructor"
        />
      </Modal>
      </div>
    </div>
  );
};

export default StockRequests;
